import {
  Button,
  CreatableSelect,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Divider,
  Select,
  TextField,
  toast,
  Typography,
} from "@suraasa/placebo-ui"
import api from "api"
import { QualificationField } from "api/resources/global/types"
import {
  Evidence,
  modeOfLearningChoices,
  Qualification,
  QualificationLevel,
} from "api/resources/profile/types"
import { APIError } from "api/utils"
import clsx from "clsx"
import LoadingOverlay from "components/LoadingOverlay"
import { AuthManager } from "features/Authentication/AuthManager"
import * as React from "react"
import { useContext, useEffect, useState } from "react"
import { Controller, useForm } from "react-hook-form"
import { handleErrors, narrowError } from "utils/helpers"
import useArray from "utils/hooks/useArray"
import { trackingService } from "utils/tracking"
import { TrackingEvents } from "utils/tracking/events"

import RemoveDialog from "@/common/RemoveDialog"

import ProfileContext from "../../../context"
import UploadEvidenceSection from "../UploadEvidenceSection"

type FormData = Omit<Qualification, "isVerified" | "id">

type Props = {
  id: Qualification["id"] | null
  setOpen: React.Dispatch<React.SetStateAction<boolean>>
  open: boolean
  source?: string
}

type Option = {
  value: string
  label: string
}

const formatOptions = (options: QualificationField[]): Option[] =>
  options.map(item => ({ value: item.uuid, label: item.name }))

const initialValues: FormData = {
  name: "",
  organisationName: "",
  qualificationFieldId: "",
  qualificationLevelId: "",
  modeOfLearning: null,
  grade: "",
  endDate: "",
  startDate: "",
  qualificationField: null,
  qualificationLevel: null,
  status: null,
}

const EditDialog = ({ source, id, open, setOpen }: Props) => {
  const {
    register,
    handleSubmit,
    setError,
    setValue,
    reset,
    control,
    watch,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({ defaultValues: initialValues })

  const {
    academics: { qualifications },
  } = useContext(ProfileContext)

  const [loading, toggleLoading] = useState(false)

  const isEditable = Boolean(id)
  const [qualificationLevelChoices, setQualificationLevelChoices] =
    React.useState<QualificationLevel[]>([])

  const [qualificationFieldChoices, setQualificationFieldChoices] =
    React.useState<Option[]>([])

  const newEvidences = useArray<File | string>([])
  const evidencesFiles = useArray<Evidence>([])
  const evidencesFilesToBeDeleted = useArray<Evidence["id"]>([])

  const startDate = watch("startDate")
  const endDate = watch("endDate")

  useEffect(() => {
    clearErrors(["startDate", "endDate"])
  }, [startDate, endDate, clearErrors])

  const resetForm = () => {
    newEvidences.clear()
    evidencesFiles.clear()
    evidencesFilesToBeDeleted.clear()
    setQualificationFieldChoices([])
    reset(initialValues)
  }

  const uploadEvidence = async (data: Qualification) => {
    const evidences = new FormData()

    newEvidences.array.forEach(item => {
      if (typeof item === "string") {
        evidences.append("url[]", item)
      } else {
        evidences.append("file[]", item)
      }
    })

    const res = await api.profile.qualification.evidence.create({
      data: evidences,
      urlParams: {
        id: data.id,
      },
    })
    return res
  }

  const deleteEvidence = async (evidenceId: Qualification["id"]) =>
    api.profile.qualification.evidence.delete({
      data: {
        evidences: evidencesFilesToBeDeleted.array,
      },
      urlParams: {
        id: evidenceId,
      },
    })

  // const showHideSkillsAlert = ![
  //   workExperiences.data.length,
  //   qualifications.data.length,
  //   certifications.data.length,
  // ].some(length => length > 1)

  const handleClose = () => {
    resetForm()
    setOpen(false)
  }

  const handleRemove = async () => {
    if (!id) return

    try {
      await api.profile.qualification.delete({ urlParams: { id } })
      qualifications.refetch()
      handleClose()
    } catch (e) {
      if (narrowError(e)) {
        handleErrors(e)
      }
    }
  }

  const createQualificationField = async (
    qualificationField: QualificationField["name"]
  ) => {
    const res = await api.global.qualificationFields.create({
      data: {
        new: [
          {
            qualificationField,
            userUuid: AuthManager.getPlatformAuth()?.user.uuid,
          },
        ],
      },
    })
    if (res.isSuccessful) {
      setQualificationFieldChoices(s => [...s, ...formatOptions(res.data)])
      setValue("qualificationFieldId", res.data[0].uuid)
    } else {
      setValue("qualificationFieldId", "")
      toast.error("You cannot add custom option at the moment.")
    }
  }

  useEffect(() => {
    const fetchQualificationLevelsAndFields = async () => {
      const resQualificationLevel = await api.profile.listQualificationLevels()
      if (resQualificationLevel.isSuccessful) {
        setQualificationLevelChoices(resQualificationLevel.data)
      } else if (resQualificationLevel.errors.message) {
        toast.error(resQualificationLevel.errors.message)
      }

      const resQualificationField = await api.global.qualificationFields.list({
        params: {
          page: -1,
        },
      })
      if (resQualificationField.isSuccessful) {
        setQualificationFieldChoices(formatOptions(resQualificationField.data))
      } else if (resQualificationField.errors.message) {
        toast.error(resQualificationField.errors.message)
      }
    }
    if (!isEditable && open) {
      fetchQualificationLevelsAndFields()
    }
  }, [open, isEditable])

  useEffect(() => {
    const fetchData = async () => {
      toggleLoading(true)
      const [resQualificationLevel, resQualificationField, resQualification] =
        await Promise.all([
          api.profile.listQualificationLevels(),
          api.global.qualificationFields.list({
            params: {
              page: -1,
            },
          }),
          api.profile.qualification.retrieve({
            urlParams: { id: id || "" },
          }),
        ])

      if (resQualificationLevel.isSuccessful) {
        setQualificationLevelChoices(resQualificationLevel.data)
      } else if (resQualificationLevel.errors.message) {
        toast.error(resQualificationLevel.errors.message)
      }

      if (resQualificationField.isSuccessful) {
        setQualificationFieldChoices(formatOptions(resQualificationField.data))
      } else if (resQualificationField.errors.message) {
        toast.error(resQualificationField.errors.message)
      }

      if (resQualification.isSuccessful) {
        const { evidences, ...data } = resQualification.data
        evidencesFiles.set(evidences)
        reset(data)
        const { qualificationField, qualificationFieldId } = data
        if (
          !qualificationFieldChoices.find(
            i => i.value === qualificationFieldId
          ) &&
          qualificationField
        ) {
          setQualificationFieldChoices(s => [
            ...s,
            {
              label: qualificationField.name,
              value: qualificationField.uuid,
            },
          ])
        }
      }
      toggleLoading(false)
    }

    if (isEditable && open) {
      fetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isEditable, open, reset, toggleLoading])

  const onSubmit = handleSubmit(async formData => {
    const apiData = {
      ...formData,
      modeOfLearning: modeOfLearningChoices.find(
        ({ value }) => value === formData.modeOfLearning
      )?.value,
    }

    if (id) {
      if (evidencesFilesToBeDeleted.array.length !== 0) {
        const deleteCertificationFilesRes = await deleteEvidence(id)
        if (!deleteCertificationFilesRes.isSuccessful) {
          if (deleteCertificationFilesRes.errors.message) {
            toast.error(deleteCertificationFilesRes.errors.message)
          }
          return
        }
      }

      const res = await api.profile.qualification.update({
        data: apiData,
        urlParams: { id },
      })
      if (res.isSuccessful) {
        if (newEvidences.array.length > 0) {
          const evidenceRes = await uploadEvidence(res.data)
          if (evidenceRes.isSuccessful) {
            qualifications.refetch()
            return handleClose()
          }
          if (evidenceRes.errors.message)
            return toast.error(evidenceRes.errors.message)
        } else {
          qualifications.refetch()
          return handleClose()
        }
      } else {
        handleErrors(res, { setter: setError })
      }
    } else {
      try {
        const res = await api.profile.qualification.create({
          data: apiData,
        })

        trackingService.trackEvent(
          TrackingEvents.profileAcademicQualificationAdded,
          {
            source,
          }
        )
        if (newEvidences.array.length > 0) {
          const evidenceRes = await uploadEvidence(res)

          if (evidenceRes.isSuccessful) {
            qualifications.refetch()
            return handleClose()
          }
          if (evidenceRes.errors.message)
            return toast.error(evidenceRes.errors.message)
        } else {
          qualifications.refetch()
          return handleClose()
        }
      } catch (e) {
        if (e instanceof APIError) handleErrors(e, { setter: setError })
      }
    }
  })

  return (
    <>
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              <Typography variant="strong">
                {isEditable ? "Edit" : "Add New"} Qualification
              </Typography>
            </DialogTitle>
          </DialogHeader>
          <DialogBody>
            <div className="flex flex-col gap-3">
              {loading && <LoadingOverlay />}
              {/* <Section1 /> */}

              <TextField
                errors={errors.name?.message}
                required
                label="Qualification Name"
                placeholder="Ex: B. Ed."
                autoFocus
                {...register("name", {
                  required: { value: true, message: "Required" },
                })}
              />

              <TextField
                errors={errors.organisationName?.message}
                required
                label="Institute Name"
                placeholder="Ex: Harvard University"
                {...register("organisationName", {
                  required: { value: true, message: "Required" },
                })}
              />

              <Controller
                control={control}
                name="qualificationLevelId"
                render={({ field: { onChange, onBlur, value } }) => (
                  <Select
                    errors={errors.qualificationLevelId?.message}
                    getOptionLabel={obj => obj.name}
                    getOptionValue={obj => String(obj.uuid)}
                    required
                    label="Qualification Level"
                    options={qualificationLevelChoices}
                    placeholder="Select Level"
                    value={
                      value
                        ? qualificationLevelChoices.find(c => c.uuid === value)
                        : null
                    }
                    onBlur={onBlur}
                    onChange={val => {
                      onChange(val?.uuid)
                    }}
                  />
                )}
                rules={{
                  required: { value: true, message: "Required" },
                }}
              />

              <Controller
                control={control}
                name="qualificationFieldId"
                render={({ field: { onChange, onBlur, value } }) => (
                  <CreatableSelect
                    errors={errors.qualificationFieldId?.message}
                    required
                    label="Qualification Field"
                    options={qualificationFieldChoices}
                    placeholder="Select Field"
                    value={
                      value
                        ? qualificationFieldChoices.find(c => c.value === value)
                        : null
                    }
                    onBlur={onBlur}
                    onChange={(option, actionMeta) => {
                      onChange(option?.value)
                      if (
                        actionMeta.action === "create-option" &&
                        option?.value
                      ) {
                        createQualificationField(option.value)
                      }
                    }}
                  />
                )}
                rules={{
                  required: { value: true, message: "Required" },
                }}
              />
              <div>
                <Divider className="my-1" weight="medium" />
              </div>

              {/* <Section2 /> */}
              <>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <TextField
                    errors={errors.startDate?.message}
                    required
                    label="start date"
                    placeholder="Ex: Jan 2021"
                    type="date"
                    {...register("startDate", {
                      required: { value: true, message: "Required" },
                    })}
                  />
                  <TextField
                    errors={errors.endDate?.message}
                    required
                    label="end date (or expected)"
                    placeholder="Ex: May 2021"
                    type="date"
                    {...register("endDate", {
                      required: { value: true, message: "Required" },
                    })}
                  />
                </div>
                <TextField
                  errors={errors.grade?.message}
                  label="grade/CGPA"
                  placeholder="Ex: 90%"
                  {...register("grade")}
                />
                <Controller
                  control={control}
                  name="modeOfLearning"
                  render={({ field: { onChange, onBlur, value } }) => (
                    <Select
                      errors={errors.modeOfLearning?.message}
                      required
                      label="Mode of learning"
                      options={modeOfLearningChoices}
                      placeholder="Select Learning Mode"
                      value={
                        value &&
                        modeOfLearningChoices.find(c => c.value === value)
                      }
                      onBlur={onBlur}
                      onChange={newValue => onChange(newValue?.value)}
                    />
                  )}
                  rules={{
                    required: { value: true, message: "Required" },
                  }}
                />
              </>
              <div>
                <Divider className="my-1" weight="medium" />
              </div>
              <div>
                <UploadEvidenceSection
                  buttonLabel="Add Certificate"
                  evidenceFiles={evidencesFiles}
                  handleEvidenceFilesToBeDeleted={
                    evidencesFilesToBeDeleted.push
                  }
                  inputLabel="Certifications (upload upto 3)"
                  limit={3}
                  maxSize={5}
                  newEvidences={newEvidences}
                />
              </div>
            </div>
          </DialogBody>

          <DialogFooter
            className={clsx("flex", {
              "!justify-between": isEditable,
            })}
          >
            {isEditable && (
              <RemoveDialog
                trigger={
                  <Button
                    variant="text"
                    color="critical"
                    loading={isSubmitting}
                  >
                    Remove
                  </Button>
                }
                title="Remove Qualification?"
                onRemove={handleRemove}
              >
                <Typography variant="smallBody">
                  Are you sure you want to remove{" "}
                  <b>
                    {qualifications.data.find(item => item.id === id)?.name}
                  </b>{" "}
                  from your profile?
                </Typography>
              </RemoveDialog>
            )}

            <div>
              <Button
                variant="outlined"
                onClick={() => {
                  handleClose()
                }}
                className="mr-1.25 border-muted text-muted"
                size="sm"
                color="black"
              >
                Cancel
              </Button>
              <Button
                color="black"
                onClick={onSubmit}
                size="sm"
                loading={isSubmitting}
                className="py-1.25"
              >
                Save
              </Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

export default EditDialog
